var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Col, Row } from "@folio/stripes/components";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import { createOnixExportSchema } from "../../../utils/validationUtils";
import { generateOnixExport } from "../../../api/reportsService";
import { transformFormToApiParams, } from "../../../types/reports/onixExport";
import { useReportMutation } from "../../../hooks/useReportMutation";
import { ReportSubmitSection } from "./ReportSubmitSection";
import { InfoBox, InfoBoxTypeEnum } from "../../InfoBox";
import { RecekDatepicker } from "../../inputs";
import { RecekTextField } from "../../inputs/RecekTextField";
import { RecekSelect } from "../../inputs/RecekSelect";
import { RecekTextArea } from "../../inputs/RecekTextArea";
import { DISPLAY_DATE_FORMAT } from "../../../utils/dateUtils";
var defaultValues = {
    modifiedSince: "",
    modifiedBefore: "",
    isbns: "",
    titleIds: "",
    offset: "0",
    limit: "100",
};
export var OnixExportForm = function () {
    var formatMessage = useIntl().formatMessage;
    var _a = useForm({
        resolver: zodResolver(createOnixExportSchema(formatMessage)),
        defaultValues: defaultValues,
    }), control = _a.control, handleSubmit = _a.handleSubmit;
    var reportMutation = useReportMutation({
        reportType: "onix-export",
        generateReport: function (ky, data) {
            var apiParams = transformFormToApiParams(data);
            return generateOnixExport(ky, apiParams);
        },
        successMessageId: "ui-br-agency.outputs.onix-export.success.generate",
        errorMessageId: "ui-br-agency.outputs.onix-export.error.generate",
    });
    return (React.createElement("form", { onSubmit: handleSubmit(function (data) { return reportMutation.mutate(data); }) },
        React.createElement("h2", null,
            React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.report.onix-export" })),
        React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.onix-export.description" })),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 6 },
                React.createElement(Controller, { control: control, name: "modifiedSince", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekDatepicker, __assign({}, field, { labelId: "ui-br-agency.outputs.onix-export.field.modifiedSince", backendDateStandard: "ISO 8601", dateFormat: DISPLAY_DATE_FORMAT, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message })));
                    } })),
            React.createElement(Col, { xs: 6 },
                React.createElement(Controller, { control: control, name: "modifiedBefore", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekDatepicker, __assign({}, field, { labelId: "ui-br-agency.outputs.onix-export.field.modifiedBefore", backendDateStandard: "ISO 8601", dateFormat: DISPLAY_DATE_FORMAT, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message })));
                    } }))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "isbns", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekTextArea, __assign({}, field, { labelId: "ui-br-agency.outputs.onix-export.field.isbns", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message, placeholder: formatMessage({
                                id: "ui-br-agency.outputs.onix-export.field.isbns.placeholder",
                            }) })));
                    } })),
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "titleIds", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekTextArea, __assign({}, field, { labelId: "ui-br-agency.outputs.onix-export.field.titleIds", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message, placeholder: formatMessage({
                                id: "ui-br-agency.outputs.onix-export.field.titleIds.placeholder",
                            }) })));
                    } }))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 6, md: 3 },
                React.createElement(Controller, { control: control, name: "offset", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-agency.outputs.onix-export.field.offset", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message, type: "number" })));
                    } })),
            React.createElement(Col, { xs: 6, md: 3 },
                React.createElement(Controller, { control: control, name: "limit", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekSelect, __assign({}, field, { labelId: "ui-br-agency.outputs.onix-export.field.limit", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message }),
                            React.createElement("option", { value: "10" }, "10"),
                            React.createElement("option", { value: "25" }, "25"),
                            React.createElement("option", { value: "50" }, "50"),
                            React.createElement("option", { value: "100" }, "100")));
                    } }))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(InfoBox, { type: InfoBoxTypeEnum.INFO },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.onix-export.info.description" })))),
        React.createElement(ReportSubmitSection, { reportJob: reportMutation.reportJob, isLoading: reportMutation.isLoading, generateButtonId: "ui-br-agency.outputs.onix-export.button.generate", successMessage: reportMutation.successMessage, onSuccessDismiss: reportMutation.clearSuccessMessage, errorModalOpen: reportMutation.errorModalOpen, errorMessage: reportMutation.errorMessage, onErrorClose: reportMutation.closeErrorModal })));
};
